<template>
  <div v-if="isVisible" class="modal-overlay" @click.self="closeModal">
    <div class="modal">
      <div class="modal-header">
        <h3>{{ isEditMode ? 'Редактировать пользователя' : 'Создать пользователя' }}</h3>
        <button type="button" class="close-button" @click="closeModal" aria-label="Закрыть">✕</button>
      </div>

      <form class="modal-form" @submit.prevent="handleSubmit">
        <section class="section-block">
          <h4 class="section-title">Основное</h4>
          <div class="field-grid">
            <div class="field">
              <label for="username">
                {{ isEditMode ? 'Username' : 'Username (уникальный идентификатор)' }}
              </label>
              <input
                id="username"
                class="input-field"
                type="text"
                placeholder="username"
                v-model="localUser.username"
                :required="true"
              />
            </div>

            <div class="field">
              <label for="title">Имя</label>
              <input
                id="title"
                class="input-field"
                type="text"
                placeholder="Имя пользователя"
                v-model="localUser.title"
                :required="!isEditMode"
              />
            </div>

            <div class="field">
              <label for="description">Должность/Позиция</label>
              <input
                id="description"
                class="input-field"
                type="text"
                placeholder="Должность/Позиция"
                v-model="localUser.description"
                :required="!isEditMode"
              />
            </div>

            <div class="field">
              <label for="category">Категория</label>
              <select id="category" class="input-field" v-model="localUser.category">
                <option value="">Не выбрана</option>
                <option v-for="option in userCategoryOptions" :key="option" :value="option">{{ option }}</option>
              </select>
            </div>

            <div class="field">
              <label for="password">Пароль</label>
              <input
                id="password"
                class="input-field"
                type="password"
                v-model="localUser.password"
                :required="!isEditMode"
              />
            </div>
          </div>

          <div class="field">
            <label for="long_description">Длинное описание</label>
            <div class="markdown-editor">
              <div class="markdown-toolbar">
                <div class="markdown-actions">
                  <button type="button" class="markdown-btn" @click="applyLongDescriptionMarkdown('**')" :disabled="isLongDescriptionPreview">
                    B
                  </button>
                  <button type="button" class="markdown-btn" @click="applyLongDescriptionMarkdown('*')" :disabled="isLongDescriptionPreview">
                    I
                  </button>
                  <button type="button" class="markdown-btn" @click="insertLongDescriptionLink" :disabled="isLongDescriptionPreview">
                    Link
                  </button>
                  <button type="button" class="markdown-btn" @click="applyLongDescriptionMarkdown('\n- ', '', 'пункт списка')" :disabled="isLongDescriptionPreview">
                    List
                  </button>
                </div>
                <div class="markdown-mode">
                  <button
                    type="button"
                    class="markdown-btn"
                    :class="{ active: !isLongDescriptionPreview }"
                    @click="setLongDescriptionMode(false)"
                  >
                    Редактировать
                  </button>
                  <button
                    type="button"
                    class="markdown-btn"
                    :class="{ active: isLongDescriptionPreview }"
                    @click="setLongDescriptionMode(true)"
                  >
                    Превью
                  </button>
                </div>
              </div>
              <textarea
                v-show="!isLongDescriptionPreview"
                id="long_description"
                ref="longDescriptionEditor"
                class="input-field textarea-field markdown-textarea"
                placeholder="Длинное описание в markdown"
                v-model="localUser.long_description"
              />
              <div v-show="isLongDescriptionPreview" class="markdown-preview" v-html="renderedLongDescription" />
            </div>
          </div>
        </section>

        <section class="section-block">
          <h4 class="section-title">Контакты</h4>
          <div class="field-grid">
            <div class="field">
              <label for="telegram_username">Telegram username</label>
              <input
                id="telegram_username"
                class="input-field"
                type="text"
                placeholder="Telegram username"
                v-model="localUser.telegram_username"
              />
              <div class="toggle-row">
                <label for="telegram_show" class="toggle-label">Показывать Telegram</label>
                <input id="telegram_show" class="checkbox-input" type="checkbox" v-model="localUser.telegram_show" />
              </div>
            </div>

            <div class="field">
              <label for="telegram_channel">Telegram channel</label>
              <input
                id="telegram_channel"
                class="input-field"
                type="text"
                placeholder="@channel"
                v-model="localUser.telegram_channel"
              />
              <div class="toggle-row">
                <label for="telegram_channel_show" class="toggle-label">Показывать канал</label>
                <input id="telegram_channel_show" class="checkbox-input" type="checkbox" v-model="localUser.telegram_channel_show" />
              </div>
            </div>

            <div class="field">
              <label for="email">Email</label>
              <input
                id="email"
                class="input-field"
                type="email"
                placeholder="email@example.com"
                v-model="localUser.email"
              />
              <div class="toggle-row">
                <label for="email_show" class="toggle-label">Показывать Email</label>
                <input id="email_show" class="checkbox-input" type="checkbox" v-model="localUser.email_show" />
              </div>
            </div>

            <div class="field">
              <label for="phone">Телефон</label>
              <input
                id="phone"
                class="input-field"
                type="text"
                placeholder="+7 999 999-99-99"
                v-model="localUser.phone"
              />
              <div class="toggle-row">
                <label for="phone_show" class="toggle-label">Показывать телефон</label>
                <input id="phone_show" class="checkbox-input" type="checkbox" v-model="localUser.phone_show" />
              </div>
            </div>
          </div>
          <div v-if="isEditMode" class="contact-extra-toggle">
            <label for="commercial_filled" class="toggle-label">Показывать коммерческое предложение (КП)</label>
            <input
              id="commercial_filled"
              class="checkbox-input"
              type="checkbox"
              v-model="localUser.commercial_filled"
            />
          </div>
        </section>

        <section v-if="isEditMode && isSuperuser" class="section-block">
          <h4 class="section-title">Права и видимость</h4>
          <div class="toggle-row">
            <label for="is_superuser" class="toggle-label">Суперпользователь</label>
            <input id="is_superuser" class="checkbox-input" type="checkbox" v-model="localUser.is_superuser" />
          </div>
          <div class="toggle-row">
            <label for="visible" class="toggle-label">Видимость на сайте</label>
            <input id="visible" class="checkbox-input" type="checkbox" v-model="localUser.visible" />
          </div>
        </section>

        <section class="section-block">
          <h4 class="section-title">Медиа</h4>
          <FileUploadField
            label="Фото пользователя"
            :current-file="currentPhoto"
            accept="image/*"
            accept-text="Изображения (JPG, PNG, GIF)"
            :max-size="5 * 1024 * 1024"
            @file-uploaded="onPhotoUploaded"
            @file-removed="onPhotoRemoved"
            @file-cancelled="onPhotoCancelled"
          />

          <FileUploadField
            label="Видео шоурил"
            :current-file="currentShowreel"
            accept="video/*"
            accept-text="Видео файлы (MP4, AVI, MOV)"
            :max-size="500 * 1024 * 1024"
            @file-uploaded="onShowreelUploaded"
            @file-removed="onShowreelRemoved"
            @file-cancelled="onShowreelCancelled"
            @file-selected="onShowreelSelected"
          />

          <FileUploadField
            label="Обложка шоурила"
            :current-file="currentShowreelCover"
            accept="image/*"
            accept-text="Изображения (JPG, PNG, WEBP)"
            :max-size="10 * 1024 * 1024"
            @file-uploaded="onShowreelCoverUploaded"
            @file-removed="onShowreelCoverRemoved"
            @file-cancelled="onShowreelCoverCancelled"
          />
        </section>

        <section class="section-block">
          <BackstageGalleryInline
            title="Backstage галерея пользователя"
            context="user"
            :context-id="isEditMode ? localUser.id : null"
            :allow-reorder="isEditMode"
            :pending-items="pendingUserBackstageImages"
            @update:pendingItems="pendingUserBackstageImages = $event"
          />
        </section>

        <div class="modal-actions">
          <button type="submit" :disabled="isUploading" class="button">
            {{ isEditMode ? 'Сохранить' : 'Создать' }}
          </button>
          <button type="button" class="button button-outline" @click="closeModal">Отмена</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { marked } from 'marked';
import { useToast } from 'vue-toastification';
import { createUser, updateUser, createBackstageImage, fetchUserCategoriesApi } from '@/services/api';
import FileUploadField from '@/components/FileUploadField.vue';
import BackstageGalleryInline from './BackstageGalleryInline.vue';

const buildLocalUser = (user = {}) => ({
  ...user,
  is_superuser: user.is_superuser || false,
  visible: user.visible !== undefined ? user.visible : true,
  id: user.id,
  username: user.username || '',
  title: user.title || '',
  description: user.description || '',
  category: user.category || '',
  long_description: user.long_description || '',
  password: '',
  telegram_username: user.telegram_username || '',
  telegram_show: user.telegram_show !== undefined ? user.telegram_show : true,
  email: user.email || '',
  email_show: user.email_show !== undefined ? user.email_show : true,
  phone: user.phone || '',
  phone_show: user.phone_show !== undefined ? user.phone_show : true,
  telegram_channel: user.telegram_channel || '',
  telegram_channel_show: user.telegram_channel_show !== undefined ? user.telegram_channel_show : true,
  commercial_filled: user.commercial_filled !== undefined ? user.commercial_filled : false
});

export default {
  components: {
    FileUploadField,
    BackstageGalleryInline
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  props: {
    isVisible: {
      type: Boolean,
      required: true
    },
    user: {
      type: Object,
      default: () => ({})
    },
    isEditMode: {
      type: Boolean,
      default: false
    },
    isSuperuser: {
      type: Boolean,
      default: false
    },
  },
  data() {
    const user = this.user || {};
    return {
      localUser: buildLocalUser(user),
      isLongDescriptionPreview: true,
      selectedPhotoId: user.photo_id || null,
      selectedShowreelId: user.showreel_video_id || null,
      selectedShowreelCoverId: user.showreel_cover_id || null,
      photoToRemove: false,
      showreelToRemove: false,
      showreelCoverToRemove: false,
      isUploading: false,
      pendingUserBackstageImages: [],
      userCategoryOptions: []
    };
  },
  async created() {
    try {
      this.userCategoryOptions = await fetchUserCategoriesApi();
    } catch (error) {
      console.error('Failed to load user categories:', error);
      this.userCategoryOptions = [];
    }
  },
  computed: {
    renderedLongDescription() {
      const longDescription = (this.localUser.long_description || '').trim();
      if (!longDescription) {
        return '<p class="markdown-empty">Длинное описание пока не заполнено.</p>';
      }
      const escapedLongDescription = longDescription
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      return marked.parse(escapedLongDescription, {
        gfm: true,
        breaks: true
      });
    },
    currentPhoto() {
      const hasPhoto = Boolean(this.user.photo_url || this.user.photo_id);
      if (!hasPhoto || this.photoToRemove) return null;
      return {
        id: this.user.photo_id || 'current-photo',
        name: 'Текущее фото пользователя',
        original_filename: 'profile_photo',
        url: this.user.photo_url || null
      };
    },
    currentShowreel() {
      const hasShowreel = Boolean(this.user.showreel_url || this.user.showreel_video_id);
      if (!hasShowreel || this.showreelToRemove) return null;
      return {
        id: this.user.showreel_video_id || 'current-showreel',
        name: 'Текущий видео шоурил',
        original_filename: 'showreel_video',
        url: this.user.showreel_url || null
      };
    },
    currentShowreelCover() {
      const hasShowreelCover = Boolean(this.user.showreel_cover_url || this.user.showreel_cover_id);
      if (!hasShowreelCover || this.showreelCoverToRemove) return null;
      return {
        id: this.user.showreel_cover_id || 'current-showreel-cover',
        name: 'Текущая обложка шоурила',
        original_filename: 'showreel_cover',
        url: this.user.showreel_cover_url || null
      };
    }
  },
  watch: {
    user: {
      handler(newUser) {
        const user = newUser || {};
        this.localUser = buildLocalUser(user);
        this.selectedPhotoId = user.photo_id || null;
        this.selectedShowreelId = user.showreel_video_id || null;
        this.selectedShowreelCoverId = user.showreel_cover_id || null;
        this.resetFileStates();
      },
      deep: true
    },
    isVisible(newValue) {
      if (newValue) {
        this.resetFileStates();
        this.isLongDescriptionPreview = true;
      }
    }
  },
  methods: {
    resetFileStates() {
      // Сбрасываем только флаги удаления, не трогаем выбранные файлы
      this.photoToRemove = false;
      this.showreelToRemove = false;
      this.showreelCoverToRemove = false;
    },
    setLongDescriptionMode(previewMode) {
      this.isLongDescriptionPreview = previewMode;
      if (!previewMode) {
        this.$nextTick(() => {
          const editor = this.$refs.longDescriptionEditor;
          if (editor) {
            editor.focus();
          }
        });
      }
    },
    applyLongDescriptionMarkdown(before, after = before, placeholder = 'текст') {
      const editor = this.$refs.longDescriptionEditor;
      if (!editor) return;
      const currentValue = this.localUser.long_description || '';
      const selectionStart = editor.selectionStart ?? currentValue.length;
      const selectionEnd = editor.selectionEnd ?? currentValue.length;
      const selectedText = currentValue.slice(selectionStart, selectionEnd) || placeholder;
      this.localUser.long_description = `${currentValue.slice(0, selectionStart)}${before}${selectedText}${after}${currentValue.slice(selectionEnd)}`;

      this.$nextTick(() => {
        editor.focus();
        const start = selectionStart + before.length;
        const end = start + selectedText.length;
        editor.setSelectionRange(start, end);
      });
    },
    insertLongDescriptionLink() {
      this.applyLongDescriptionMarkdown('[', '](https://)', 'текст ссылки');
    },
    
    // Обработчики для фото
    onPhotoUploaded(file) {
      this.selectedPhotoId = file.id;
      this.photoToRemove = false;
    },
    onPhotoRemoved() {
      this.photoToRemove = true;
      this.selectedPhotoId = null;
    },
    onPhotoCancelled() {
      this.selectedPhotoId = null;
      this.photoToRemove = false;
    },
    
    // Обработчики для showreel
    onShowreelUploaded(file) {
      this.isUploading = false;
      this.selectedShowreelId = file.id;
      this.showreelToRemove = false;
    },
    onShowreelRemoved() {
      this.isUploading = false;
      this.showreelToRemove = true;
      this.selectedShowreelId = null;
    },
    onShowreelCancelled() {
      this.isUploading = false;
      this.selectedShowreelId = null;
      this.showreelToRemove = false;
    },
    onShowreelSelected() {
      this.isUploading = true;
    },
    onShowreelCoverUploaded(file) {
      this.selectedShowreelCoverId = file.id;
      this.showreelCoverToRemove = false;
    },
    onShowreelCoverRemoved() {
      this.showreelCoverToRemove = true;
      this.selectedShowreelCoverId = null;
    },
    onShowreelCoverCancelled() {
      this.selectedShowreelCoverId = null;
      this.showreelCoverToRemove = false;
    },
    async handleSubmit() {
      try {
        let response;
        const updatedData = {}; // Объект для хранения измененных данных

        if (this.isEditMode) {
          // Сравниваем поля и добавляем только измененные
          if (this.localUser.username !== this.user.username) {
            updatedData.username = this.localUser.username;
          }
          if (this.localUser.title !== this.user.title) {
            updatedData.title = this.localUser.title;
          }
          if (this.localUser.description !== this.user.description) {
            updatedData.description = this.localUser.description;
          }
          if ((this.localUser.category || '') !== (this.user.category || '')) {
            updatedData.category = this.localUser.category || null;
          }
          if (this.localUser.long_description !== this.user.long_description) {
            updatedData.long_description = this.localUser.long_description;
          }
          if (this.localUser.password && this.localUser.password !== this.user.password) {
            updatedData.password = this.localUser.password;
          }
          if (this.localUser.is_superuser !== this.user.is_superuser) {
            updatedData.is_superuser = this.localUser.is_superuser;
          }
          if (this.localUser.visible !== this.user.visible) {
            updatedData.visible = this.localUser.visible;
          }
          if (this.localUser.telegram_username !== this.user.telegram_username) {
            updatedData.telegram_username = this.localUser.telegram_username;
          }
          if (this.localUser.telegram_show !== this.user.telegram_show) {
            updatedData.telegram_show = this.localUser.telegram_show;
          }
          if (this.localUser.email !== this.user.email) {
            updatedData.email = this.localUser.email;
          }
          if (this.localUser.email_show !== this.user.email_show) {
            updatedData.email_show = this.localUser.email_show;
          }
          if (this.localUser.phone !== this.user.phone) {
            updatedData.phone = this.localUser.phone;
          }
          if (this.localUser.phone_show !== this.user.phone_show) {
            updatedData.phone_show = this.localUser.phone_show;
          }
          if (this.localUser.telegram_channel !== this.user.telegram_channel) {
            updatedData.telegram_channel = this.localUser.telegram_channel;
          }
          if (this.localUser.telegram_channel_show !== this.user.telegram_channel_show) {
            updatedData.telegram_channel_show = this.localUser.telegram_channel_show;
          }
          updatedData.commercial_filled = Boolean(this.localUser.commercial_filled);
          // Обработка фото
          if (this.selectedPhotoId) {
            updatedData.photo_id = this.selectedPhotoId;
          } else if (this.photoToRemove) {
            updatedData.photo_id = null;
          }
          if (this.selectedShowreelCoverId) {
            updatedData.showreel_cover_id = this.selectedShowreelCoverId;
          } else if (this.showreelCoverToRemove) {
            updatedData.showreel_cover_id = null;
          }
          
          // Обработка showreel
          if (this.selectedShowreelId) {
            updatedData.showreel_video_id = this.selectedShowreelId;
          } else if (this.showreelToRemove) {
            updatedData.showreel_video_id = null;
          }

          // Отправляем только измененные данные
          response = await updateUser(this.localUser.id, updatedData);
          this.$emit('submit', updatedData);
        } else {
          // Режим добавления
          response = await createUser({
            username: this.localUser.username,
            photo_id: this.selectedPhotoId,
            showreel_cover_id: this.selectedShowreelCoverId,
            long_description: this.localUser.long_description,
            showreel_video_id: this.selectedShowreelId,
            title: this.localUser.title,
            description: this.localUser.description,
            category: this.localUser.category || null,
            password: this.localUser.password
          });
          if (response?.id && this.pendingUserBackstageImages.length) {
            const imagesToCreate = [...this.pendingUserBackstageImages];
            this.pendingUserBackstageImages = [];
            for (let index = 0; index < imagesToCreate.length; index += 1) {
              const item = imagesToCreate[index];
              await createBackstageImage({
                file_id: item.file_id,
                context: 'user',
                context_id: String(response.id),
                position: index
              });
            }
          }
          this.$emit('submit', response);
        }

        this.closeModal();
      } catch (error) {
        console.error('Ошибка:', error);
        this.toast.error(error?.message || 'Ошибка при сохранении пользователя');
      }
    },
    
    closeModal() {
      this.resetFileStates();
      this.isLongDescriptionPreview = true;
      this.$emit('close');
      this.$emit('refresh');
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  padding: 16px;
  background: hsl(var(--foreground) / 0.45);
  backdrop-filter: blur(3px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal {
  width: 100%;
  max-width: 860px;
  max-height: 92vh;
  overflow-y: auto;
  border-radius: 14px;
  border: 1px solid hsl(var(--border));
  background: hsl(var(--card));
  color: hsl(var(--card-foreground));
  box-shadow: 0 24px 60px hsl(var(--foreground) / 0.35);
  padding: 20px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.modal h3 {
  margin: 0;
  font-size: 20px;
  line-height: 1.2;
}

.close-button {
  border: 1px solid hsl(var(--border));
  background: hsl(var(--background));
  color: hsl(var(--foreground));
  border-radius: 8px;
  width: 32px;
  height: 32px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.close-button:hover {
  background: hsl(var(--muted));
}

.modal-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.section-block {
  border: 1px solid hsl(var(--border));
  background: hsl(var(--card));
  border-radius: 10px;
  padding: 12px;
}

.section-title {
  margin: 0 0 10px 0;
  font-size: 14px;
  font-weight: 700;
}

.field-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-full {
  grid-column: 1 / -1;
}

.modal label {
  display: block;
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: hsl(var(--foreground));
}

.input-field {
  width: 100%;
  box-sizing: border-box;
}

.input-field:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.textarea-field {
  min-height: 96px;
  resize: vertical;
}

.markdown-editor {
  border: 1px solid hsl(var(--input));
  border-radius: 8px;
  overflow: hidden;
  background: hsl(var(--background));
}

.markdown-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px;
  border-bottom: 1px solid hsl(var(--input));
  background: hsl(var(--muted));
}

.markdown-actions,
.markdown-mode {
  display: flex;
  align-items: center;
  gap: 6px;
}

.markdown-btn {
  border: 1px solid hsl(var(--border));
  background: hsl(var(--background));
  color: hsl(var(--foreground));
  border-radius: 6px;
  min-width: 34px;
  padding: 5px 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.markdown-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.markdown-btn.active {
  border-color: hsl(var(--accent));
  color: hsl(var(--accent));
}

.markdown-btn:not(:disabled):hover {
  background: hsl(var(--muted));
}

.markdown-textarea {
  border: none;
  border-radius: 0;
  min-height: 130px;
}

.markdown-textarea:focus {
  border: none;
}

.markdown-preview {
  min-height: 130px;
  padding: 10px;
  font-size: 14px;
  line-height: 1.5;
}

.markdown-preview :deep(h1),
.markdown-preview :deep(h2),
.markdown-preview :deep(h3),
.markdown-preview :deep(h4),
.markdown-preview :deep(h5),
.markdown-preview :deep(h6) {
  margin: 0 0 8px;
}

.markdown-preview :deep(p),
.markdown-preview :deep(ul),
.markdown-preview :deep(ol),
.markdown-preview :deep(blockquote) {
  margin: 0 0 8px;
}

.markdown-preview :deep(ul),
.markdown-preview :deep(ol) {
  padding-left: 18px;
}

.markdown-preview :deep(code) {
  background: hsl(var(--muted));
  padding: 2px 4px;
  border-radius: 4px;
}

.markdown-preview :deep(.markdown-empty) {
  color: hsl(var(--muted-foreground));
  margin: 0;
}

.markdown-preview :deep(strong),
.markdown-preview :deep(b) {
  font-weight: 700;
}

.markdown-preview :deep(a) {
  color: hsl(var(--accent));
  text-decoration: underline;
}

.toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
}

.contact-extra-toggle {
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: 1px solid hsl(var(--border));
  border-radius: 8px;
  background: hsl(var(--muted));
  padding: 8px 10px;
}

.toggle-label {
  margin: 0;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: hsl(var(--foreground));
}

.checkbox-input {
  width: 18px;
  height: 18px;
  margin: 0;
  flex-shrink: 0;
  accent-color: hsl(var(--accent));
}

.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 4px;
}

@media (max-width: 768px) {
  .modal {
    padding: 14px;
  }

  .field-grid {
    grid-template-columns: 1fr;
  }

  .modal-actions {
    flex-direction: column-reverse;
    align-items: stretch;
  }

  .markdown-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .markdown-actions,
  .markdown-mode {
    justify-content: space-between;
  }
}
</style>
